import type { MeResponse, SetupStatus } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useSetMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

export function SetupPage() {
  const navigate = useNavigate();
  const setMe = useSetMe();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const status = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api<SetupStatus>('/auth/setup-status'),
  });

  const [form, setForm] = useState({
    companyName: '',
    fullName: '',
    position: '',
    email: '',
    password: '',
    repeat: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setError(null);
    if (form.password !== form.repeat) {
      setErrors({ repeat: 'Пароли не совпадают' });
      return;
    }
    setBusy(true);
    try {
      const me = await api<MeResponse>('/auth/setup', {
        body: {
          token,
          companyName: form.companyName.trim() || undefined,
          fullName: form.fullName,
          position: form.position || undefined,
          email: form.email,
          password: form.password,
        },
      });
      setMe(me);
      navigate({ to: '/' });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors());
        setError(err);
      } else setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (status.isLoading)
    return (
      <AuthLayout title="Первичная настройка">
        <Spinner />
      </AuthLayout>
    );

  if (!status.data?.needsSetup || !token) {
    return (
      <AuthLayout title="Первичная настройка">
        <EmptyState
          title="Настройка уже выполнена"
          action={
            <Link
              to="/login"
              className="text-[14px] font-medium text-accent-text underline underline-offset-4"
            >
              Перейти ко входу
            </Link>
          }
        >
          Владелец компании создан. Войдите со своей почтой и паролем или попросите приглашение у
          администратора.
        </EmptyState>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Создание владельца"
      subtitle={
        <>
          Вы создаёте первую учётную запись своей компании
          {status.data.tenantName ? ` (сейчас в установке указано «${status.data.tenantName}»)` : ''}.
          Владелец получает все права и приглашает остальных сотрудников.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        <Field
          label="Название компании"
          htmlFor="companyName"
          hint="Как компания будет называться в портале"
          error={errors.companyName}
        >
          <Input
            id="companyName"
            autoComplete="organization"
            value={form.companyName}
            onChange={set('companyName')}
            placeholder={status.data.tenantName ?? 'Название вашей компании'}
            invalid={!!errors.companyName}
          />
        </Field>
        <Field label="Имя и фамилия" htmlFor="fullName" error={errors.fullName}>
          <Input
            id="fullName"
            autoComplete="name"
            value={form.fullName}
            onChange={set('fullName')}
            invalid={!!errors.fullName}
          />
        </Field>
        <Field label="Должность" htmlFor="position" hint="Например: собственник, директор">
          <Input
            id="position"
            autoComplete="organization-title"
            value={form.position}
            onChange={set('position')}
          />
        </Field>
        <Field label="Рабочая почта" htmlFor="email" error={errors.email}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={set('email')}
            invalid={!!errors.email}
          />
        </Field>
        <Field
          label="Пароль"
          htmlFor="password"
          hint="Не короче 10 символов, буквы и цифры"
          error={errors.password}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
            invalid={!!errors.password}
          />
        </Field>
        <Field label="Пароль ещё раз" htmlFor="repeat" error={errors.repeat}>
          <Input
            id="repeat"
            type="password"
            autoComplete="new-password"
            value={form.repeat}
            onChange={set('repeat')}
            invalid={!!errors.repeat}
          />
        </Field>
        <ErrorBox error={Object.keys(errors).length ? null : error} />
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          Создать владельца и войти
        </Button>
      </form>
    </AuthLayout>
  );
}
